import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import { showSuccess, showError } from '../utils/notifications';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  Plus,
  Ticket,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  ArrowLeft,
  Send,
  Bug,
  Lightbulb,
  CreditCard,
  Wrench,
  User
} from 'lucide-react';

const Support = ({ user }) => {
  const [view, setView] = useState('list'); // 'list', 'create', 'detail'
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state for creating tickets
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'general_support',
    priority: 'medium'
  });

  // Reply state
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // Categories with icons
  const categories = {
    bug_report: {
      label: 'Bug Report',
      icon: Bug,
      color: 'text-red-600 bg-red-50'
    },
    feature_request: {
      label: 'Feature Request',
      icon: Lightbulb,
      color: 'text-yellow-600 bg-yellow-50'
    },
    billing_issue: {
      label: 'Billing Issue',
      icon: CreditCard,
      color: 'text-green-600 bg-green-50'
    },
    general_support: {
      label: 'General Support',
      icon: MessageSquare,
      color: 'text-blue-600 bg-blue-50'
    },
    technical_issue: {
      label: 'Technical Issue',
      icon: Wrench,
      color: 'text-purple-600 bg-purple-50'
    }
  };

  // Status badge styles
  const getStatusBadge = (status) => {
    const styles = {
      open: { label: 'Open', variant: 'default', color: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'In Progress', variant: 'secondary', color: 'bg-yellow-100 text-yellow-800' },
      needs_review: { label: 'Needs Review', variant: 'warning', color: 'bg-orange-100 text-orange-800' },
      resolved: { label: 'Resolved', variant: 'success', color: 'bg-green-100 text-green-800' },
      closed: { label: 'Closed', variant: 'secondary', color: 'bg-gray-100 text-gray-800' }
    };
    return styles[status] || styles.open;
  };

  // Priority badge styles
  const getPriorityBadge = (priority) => {
    const styles = {
      low: { label: 'Low', color: 'bg-gray-100 text-gray-800' },
      medium: { label: 'Medium', color: 'bg-blue-100 text-blue-800' },
      high: { label: 'High', color: 'bg-orange-100 text-orange-800' },
      urgent: { label: 'Urgent', color: 'bg-red-100 text-red-800' }
    };
    return styles[priority] || styles.medium;
  };

  // Fetch user's tickets
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/support/tickets/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle empty array response - this is not an error
      setTickets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      // Only show error if it's not a "no tickets" scenario
      if (error.response?.status !== 404) {
        showError('Failed to load tickets');
      }
      // Set empty array even on error so UI shows "No tickets" message
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch single ticket with replies
  const fetchTicketDetail = async (ticketId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(response.data);
      setView('detail');
    } catch (error) {
      console.error('Error fetching ticket:', error);
      showError('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  // Create new ticket
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      showError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/support/tickets`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess('Support ticket created successfully!');
      setFormData({
        subject: '',
        description: '',
        category: 'general_support',
        priority: 'medium'
      });
      setView('list');
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      showError('Failed to create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Send reply to ticket
  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      showError('Please enter a message');
      return;
    }

    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/support/tickets/${selectedTicket.id}/replies`,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess('Reply sent successfully');
      setReplyMessage('');
      fetchTicketDetail(selectedTicket.id);
    } catch (error) {
      console.error('Error sending reply:', error);
      showError('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  useEffect(() => {
    if (view === 'list') {
      fetchTickets();
    }
  }, [view]);

  // Check if we need to open a specific ticket from notification
  useEffect(() => {
    const openTicketId = sessionStorage.getItem('openTicketId');
    if (openTicketId) {
      // Clear the stored ID
      sessionStorage.removeItem('openTicketId');
      // Fetch and open the ticket
      fetchTicketDetail(openTicketId);
    }
  }, []);

  // Render ticket list view
  const renderTicketList = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Support Tickets</h2>
            <p className="text-gray-600 mt-1">View and manage your support requests</p>
          </div>
          <Button onClick={() => setView('create')} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Ticket
          </Button>
        </div>

        {/* Tickets List */}
        {loading ? (
          <Card className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading tickets...</p>
          </Card>
        ) : tickets.length === 0 ? (
          <Card className="p-12 text-center">
            <Ticket className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Support Tickets</h3>
            <p className="text-gray-600 mb-6">You haven't created any support tickets yet.</p>
            <Button onClick={() => setView('create')}>Create Your First Ticket</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => {
              // Get category icon, default to Bug if category not found
              const categoryData = categories[ticket.category] || categories['general_support'];
              const CategoryIcon = categoryData.icon;
              const categoryColor = categoryData.color;
              const statusBadge = getStatusBadge(ticket.status);
              const priorityBadge = getPriorityBadge(ticket.priority);

              return (
                <Card
                  key={ticket.id}
                  className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => fetchTicketDetail(ticket.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className={`p-3 rounded-lg ${categoryColor}`}>
                        <CategoryIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-xs font-mono text-gray-400">
                            #{ticket.ticket_number}
                          </span>
                          <Badge className={statusBadge.color}>
                            {statusBadge.label}
                          </Badge>
                          <Badge className={priorityBadge.color}>
                            {priorityBadge.label}
                          </Badge>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {ticket.subject}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                          {ticket.description}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                          </div>
                          {ticket.reply_count > 0 && (
                            <div className="flex items-center space-x-1">
                              <MessageSquare className="w-4 h-4" />
                              <span>{ticket.reply_count} {ticket.reply_count === 1 ? 'reply' : 'replies'}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render create ticket form
  const renderCreateForm = () => {
    return (
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => setView('list')}
          className="mb-6 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Tickets
        </Button>

        <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900">Create Support Ticket</h2>
            <p className="text-sm text-gray-600 mt-1">Describe your issue and we'll get back to you soon</p>
          </div>

          <form onSubmit={handleCreateTicket} className="space-y-5">
            {/* Subject & Priority in one row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Subject <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Priority <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Category - Compact horizontal selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(categories).map(([key, cat]) => {
                  const CategoryIcon = cat.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData({ ...formData, category: key })}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-all text-sm font-medium ${
                        formData.category === key
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-purple-300 text-gray-700'
                      }`}
                    >
                      <CategoryIcon className="w-4 h-4" />
                      <span>{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Provide detailed information about your issue. Include steps to reproduce if applicable..."
                rows={6}
                required
                className="text-sm"
              />
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                We typically respond within 24 hours
              </p>
              <div className="flex items-center space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setView('list')}
                  disabled={submitting}
                  className="text-sm"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="text-sm">
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Ticket
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      </div>
    );
  };

  // Render ticket detail view
  const renderTicketDetail = () => {
    if (!selectedTicket) return null;

    // Get category icon, default to general_support if category not found
    const categoryData = categories[selectedTicket.category] || categories['general_support'];
    const CategoryIcon = categoryData.icon;
    const categoryColor = categoryData.color;
    const statusBadge = getStatusBadge(selectedTicket.status);
    const priorityBadge = getPriorityBadge(selectedTicket.priority);

    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          onClick={() => {
            setView('list');
            setSelectedTicket(null);
          }}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Tickets
        </Button>

        {/* Ticket Header */}
        <Card className="p-6">
          <div className="flex items-start space-x-4">
            <div className={`p-3 rounded-lg ${categoryColor}`}>
              <CategoryIcon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-mono text-gray-500">
                  #{selectedTicket.ticket_number}
                </span>
                <Badge className={statusBadge.color}>{statusBadge.label}</Badge>
                <Badge className={priorityBadge.color}>{priorityBadge.label}</Badge>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {selectedTicket.subject}
              </h2>
              <p className="text-gray-600 mb-4">{selectedTicket.description}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>{selectedTicket.user_name || selectedTicket.user_email}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>Created {new Date(selectedTicket.created_at).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Conversation Thread */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversation</h3>
          
          {selectedTicket.replies && selectedTicket.replies.length > 0 ? (
            <div className="space-y-4 mb-6">
              {selectedTicket.replies.map((reply) => {
                const isBot = reply.is_bot || (reply.admin_name === 'AI Assistant');
                return (
                  <div
                    key={reply.id}
                    className={`p-4 rounded-lg ${
                      isBot
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200'
                        : reply.is_admin
                        ? 'bg-purple-50 border border-purple-200'
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm font-medium ${
                          isBot ? 'text-blue-900' : reply.is_admin ? 'text-purple-900' : 'text-gray-900'
                        }`}>
                          {isBot ? '🤖 AI Assistant' : reply.is_admin ? '👨‍💼 Support Team' : '👤 You'}
                        </span>
                        {reply.admin_name && !isBot && (
                          <span className="text-xs text-gray-500">({reply.admin_name})</span>
                        )}
                        {isBot && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            Automated Response
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(reply.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{reply.message}</p>
                    {isBot && (
                      <p className="text-xs text-blue-600 mt-2 italic">
                        💡 This is an automated response. A human agent will follow up if needed.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 mb-6">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No replies yet. Our support team will respond soon.</p>
            </div>
          )}

          {/* Reply Form - Only show if ticket is not closed */}
          {selectedTicket.status !== 'closed' && (
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add Reply
              </label>
              <Textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={4}
                className="mb-3"
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyMessage.trim()}
                >
                  {sendingReply ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {selectedTicket.status === 'closed' && (
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 text-center">
              <p className="text-gray-700">This ticket has been closed and can no longer receive replies.</p>
            </div>
          )}
        </Card>
      </div>
    );
  };

  // Main render
  return (
    <div className="space-y-6">
      {view === 'list' && renderTicketList()}
      {view === 'create' && renderCreateForm()}
      {view === 'detail' && renderTicketDetail()}
    </div>
  );
};

export default Support;
